import api from "@/lib/api";
import { toast } from "sonner";
import { exportPNG, exportWidgetPDF, exportWidgetJSON } from "@/lib/exportUtils";
import { exportStyledExcel } from "@/lib/styledExcel";
import { buildSemanticQuery } from "@/lib/widgetCatalog";
import { applySortEngine } from "@/lib/sortEngine";

const nameOf = (w) => (w.title || w.type || "widget").replace(/\s+/g, "_");

function toCSV(columns, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columns.map(esc).join(","), ...rows.map((r) => columns.map((c) => esc(r[c])).join(","))].join("\n");
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function runWidgetQuery({ widget, filters, publicToken, whatIfParams }) {
  const sq = buildSemanticQuery(widget.config || {}, filters || []);
  if (whatIfParams) sq.params = whatIfParams;
  if (publicToken) {
    const { data } = await api.post("/public/model/query",
      { token: publicToken, dataset_id: widget.dataset_id, semantic_query: sq }, { timeout: 45000 });
    return applySortEngine(data, widget.config || {});
  }
  const { data } = await api.post(`/datasets/${widget.dataset_id}/model/query`, sq);
  return applySortEngine(data, widget.config || {});
}

// Item 26: one export path for Builder (edit + preview) and the published / shared viewer.
export async function exportWidget({ widget, fmt, filters = [], publicToken = null, whatIfParams = null }) {
  const name = nameOf(widget);
  const captureId = `wcap-${widget.id}`;
  if (fmt === "png") {
    return toast.promise(exportPNG(captureId, name), { loading: "Rendering PNG…", success: "PNG downloaded", error: "Export failed" });
  }
  if (fmt === "pdf") {
    return toast.promise(exportWidgetPDF(captureId, name, filters), { loading: "Rendering PDF…", success: "PDF downloaded", error: "Export failed" });
  }
  const p = (async () => {
    const data = await runWidgetQuery({ widget, filters, publicToken, whatIfParams });
    if (fmt === "json") return exportWidgetJSON(name, data, filters);
    if (fmt === "csv") {
      const cols = data.columns || Object.keys((data.rows || [])[0] || {});
      return download(new Blob([toCSV(cols, data.rows || [])], { type: "text/csv;charset=utf-8" }), `${name}.csv`);
    }
    return exportStyledExcel(widget, data, publicToken);
  })();
  const label = { csv: "CSV", excel: "Excel", json: "JSON" }[fmt] || "data";
  toast.promise(p, { loading: `Exporting ${label}…`, success: "Downloaded", error: "Export failed" });
  return p;
}
